import { createSlice, createAsyncThunk, PayloadAction } from "@reduxjs/toolkit";
import { WebsiteFilter, WebsiteFormData, WebsitesState } from "../../../types/website.types";
import apiClient from "../../../api/apiClient";
import { API_ENDPOINTS } from "api/endPoints";

// Initial state
const initialState: WebsitesState = {
  items: [],
  filteredItems: [],
  isLoading: false,
  error: null,
  filters: {
    publisherPK: "",
    searchTerm: "",
  },
  pagination: {
    page: 0,
    rowsPerPage: 10,
    totalCount: 0,
  },
};

// Async thunks
export const fetchWebsites = createAsyncThunk("websites/fetchWebsites", async () => {
  const response = await apiClient.get(API_ENDPOINTS.PUBLISHER.WEBSITES.LIST);
  return response.data;
});

export const fetchFilteredWebsites = createAsyncThunk(
  "websites/fetchFilteredWebsites",
  async (filters: WebsiteFilter) => {
    const response = await apiClient.get(API_ENDPOINTS.PUBLISHER.WEBSITES.FILTER, {
      params: filters,
    });
    return response.data;
  }
);

export const fetchWebsitesByPublisher = createAsyncThunk(
  "websites/fetchWebsitesByPublisher",
  async (publisherId: string) => {
    const response = await apiClient.get(API_ENDPOINTS.PUBLISHER.WEBSITES.BY_PUBLISHER(publisherId));
    return response.data;
  }
);

export const createWebsite = createAsyncThunk("websites/createWebsite", async (websiteData: WebsiteFormData) => {
  const response = await apiClient.post(API_ENDPOINTS.PUBLISHER.WEBSITES.CREATE, websiteData);
  return response.data;
});

export const updateWebsite = createAsyncThunk(
  "websites/updateWebsite",
  async ({ id, data }: { id: string; data: WebsiteFormData }) => {
    const response = await apiClient.put(API_ENDPOINTS.PUBLISHER.WEBSITES.UPDATE(id), data);
    return response.data;
  }
);

export const deleteWebsite = createAsyncThunk("websites/deleteWebsite", async (id: string) => {
  await apiClient.delete(API_ENDPOINTS.PUBLISHER.WEBSITES.DELETE(id));
  return id;
});

// Slice
const websitesSlice = createSlice({
  name: "websites",
  initialState,
  reducers: {
    setFilter: (state, action: PayloadAction<{ key: keyof WebsiteFilter; value: string }>) => {
      const { key, value } = action.payload;
      state.filters[key] = value;

      // Reset pagination
      state.pagination.page = 0;
    },
    setPage: (state, action: PayloadAction<number>) => {
      state.pagination.page = action.payload;
    },
    setRowsPerPage: (state, action: PayloadAction<number>) => {
      state.pagination.rowsPerPage = action.payload;
      state.pagination.page = 0;
    },
    resetFilters: (state) => {
      state.filters = initialState.filters;
      state.pagination.page = 0;
    },
  },
  extraReducers: (builder) => {
    builder
      // Fetch all websites
      .addCase(fetchWebsites.pending, (state) => {
        state.isLoading = true;
        state.error = null;
      })
      // In the fetchWebsites.fulfilled reducer
      .addCase(fetchWebsites.fulfilled, (state, action) => {
        console.log("fetchWebsites.fulfilled with payload length:", action.payload.length);

        // Avoid unnecessary state updates
        if (JSON.stringify(state.items) !== JSON.stringify(action.payload)) {
          state.isLoading = false;
          state.items = action.payload;
          state.filteredItems = action.payload;
          state.pagination.totalCount = action.payload.length;
          console.log("State updated with new data");
        } else {
          // Just update loading state
          state.isLoading = false;
          console.log("Data unchanged, only updated loading state");
        }
      })
      .addCase(fetchWebsites.rejected, (state, action) => {
        state.isLoading = false;
        state.error = action.error.message || "Failed to fetch websites";
      })

      // Fetch filtered websites
      .addCase(fetchFilteredWebsites.pending, (state) => {
        state.isLoading = true;
        state.error = null;
      })
      .addCase(fetchFilteredWebsites.fulfilled, (state, action) => {
        state.isLoading = false;
        state.filteredItems = action.payload;
        state.pagination.totalCount = action.payload.length;
      })
      .addCase(fetchFilteredWebsites.rejected, (state, action) => {
        state.isLoading = false;
        state.error = action.error.message || "Failed to fetch filtered websites";
      })

      // Fetch websites by publisher
      .addCase(fetchWebsitesByPublisher.pending, (state) => {
        state.isLoading = true;
        state.error = null;
      })
      .addCase(fetchWebsitesByPublisher.fulfilled, (state, action) => {
        state.isLoading = false;
        state.filteredItems = action.payload;
        state.pagination.totalCount = action.payload.length;
      })
      .addCase(fetchWebsitesByPublisher.rejected, (state, action) => {
        state.isLoading = false;
        state.error = action.error.message || "Failed to fetch websites by publisher";
      })

      // Create website
      .addCase(createWebsite.pending, (state) => {
        state.isLoading = true;
        state.error = null;
      })
      // In websitesSlice.ts, add these changes:

      // 1. In the createWebsite.fulfilled reducer
      .addCase(createWebsite.fulfilled, (state, action) => {
        state.isLoading = false;
        const newWebsite = action.payload;

        // Add to all items
        state.items.push(newWebsite);

        // Only add to filtered items if it matches the current filter criteria
        let shouldAddToFiltered = true;

        // Check publisher filter if it exists
        if (state.filters.publisherPK && newWebsite.publisherPK !== state.filters.publisherPK) {
          shouldAddToFiltered = false;
        }

        // Check search term filter if it exists
        if (shouldAddToFiltered && state.filters.searchTerm) {
          // existing filter logic
        }

        // Add to filtered items if it passes all filter checks
        if (shouldAddToFiltered) {
          state.filteredItems.push(newWebsite);
        }

        state.pagination.totalCount = state.filteredItems.length;
        // Don't reset dataFetched
      })
      .addCase(createWebsite.rejected, (state, action) => {
        state.isLoading = false;
        state.error = action.error.message || "Failed to create website";
      })

      // Update website
      .addCase(updateWebsite.pending, (state) => {
        state.isLoading = true;
        state.error = null;
      })
      .addCase(updateWebsite.fulfilled, (state, action) => {
        state.isLoading = false;
        const updatedWebsite = action.payload;

        // Update in items array
        const itemIndex = state.items.findIndex((item) => item.id === updatedWebsite.id);
        if (itemIndex !== -1) {
          state.items[itemIndex] = updatedWebsite;
        }

        // Update in filtered items array
        const filteredIndex = state.filteredItems.findIndex((item) => item.id === updatedWebsite.id);
        if (filteredIndex !== -1) {
          state.filteredItems[filteredIndex] = updatedWebsite;
        }
      })
      .addCase(updateWebsite.rejected, (state, action) => {
        state.isLoading = false;
        state.error = action.error.message || "Failed to update website";
      })

      // Delete website
      .addCase(deleteWebsite.pending, (state) => {
        state.isLoading = true;
        state.error = null;
      })
      .addCase(deleteWebsite.fulfilled, (state, action) => {
        state.isLoading = false;
        const deletedId = action.payload;

        // Remove from items array
        state.items = state.items.filter((item) => item.id !== deletedId);

        // Remove from filtered items array
        state.filteredItems = state.filteredItems.filter((item) => item.id !== deletedId);

        state.pagination.totalCount = state.filteredItems.length;
      })
      .addCase(deleteWebsite.rejected, (state, action) => {
        state.isLoading = false;
        state.error = action.error.message || "Failed to delete website";
      });
  },
});

export const { setFilter, setPage, setRowsPerPage, resetFilters } = websitesSlice.actions;

export default websitesSlice.reducer;
